import {NgModule, ModuleWithProviders} from '@angular/core';
import {RouterModule} from '@angular/router';
import {HTTP_INTERCEPTORS} from '@angular/common/http';
import {NgbModule} from '@ng-bootstrap/ng-bootstrap';
import {NgSelectModule} from '@ng-select/ng-select';
import {ToastrModule} from 'ngx-toastr';
import {BsModalRef, ModalModule} from 'ngx-bootstrap';
import {SweetAlert2Module} from '@toverux/ngx-sweetalert2';
import {DataTablesModule} from 'angular-datatables';
import {SwiperModule} from 'ngx-swiper-wrapper';

import {CommonModule} from '@angular/common';
import {ReactiveFormsModule, FormsModule} from '@angular/forms';

// MODULES_WITH_PROVIDERS
const MODULES_WITH_PROVIDERS: ModuleWithProviders[] = [
    ToastrModule.forRoot(),
    ModalModule.forRoot(),
    SweetAlert2Module.forRoot(),
    DataTablesModule.forRoot()
];

// MODULES
const MODULES = [
    ToastrModule,
    ModalModule,
    SweetAlert2Module,
    DataTablesModule,
    CommonModule,
    RouterModule,
    NgbModule,
    NgSelectModule,
    ReactiveFormsModule,
    FormsModule,
    SwiperModule
];

// COMPONENTS
import {Page404Component} from './components/error-pages/page404/page404.component';
import {Page500Component} from './components/error-pages/page500/page500.component';
import {FooterComponent} from './components/footer/footer.component';
import {NavbarComponent} from './components/navbar/navbar.component';
import {ValidationErrorComponent} from './components/common/validation-error/validation-error.component';
import {BodySectionComponent} from './components/profile-sections/body-section/body-section.component';
import {PersonalInfoSectionComponent} from './components/profile-sections/personal-info-section/personal-info-section.component';
import {TrainingInfoSectionComponent} from './components/profile-sections/training-info-section/training-info-section.component';
import {ToggleSwitchComponent} from './components/common/toggle-switch/toggle-switch.component';
import {LoadingComponent} from './components/common/loading/loading.component';
import {ModalHeaderComponent} from './components/common/modal-header/modal-header.component';
import {CheckboxComponent} from './components/common/checkbox/checkbox.component';
import {DatepickerComponent} from './components/common/datepicker/datepicker.component';
import {NutrientsBreakdownComponent} from './components/common/nutrients-breakdown/nutrients-breakdown.component';
import {SwiperComponent} from './components/common/swiper/swiper.component';
import {PopupComponent} from './components/common/popup/popup.component';
import {TooltipComponent} from './components/common/tooltip/tooltip.component';
import {TermsAndConditionsModalComponent} from './components/terms-and-conditions-modal/terms-and-conditions-modal.component';
import {OptionsListComponent} from './components/common/options-list/options-list.component';

// ENTRY COMPONENTS
const ENTRY_COMPONENTS = [
    ValidationErrorComponent,
    TermsAndConditionsModalComponent,
    DialCodeSelectorModalComponent,
    MobilebarComponent,
    UpgradeComponent,
    ShortcutsComponent,
    RecipesTextComponent,
];

// COMPONENTS
const COMPONENTS = [
    ...ENTRY_COMPONENTS,
    Page404Component,
    Page500Component,
    FooterComponent,
    NavbarComponent,
    MobilebarComponent,
    AlertsComponent,
    AgaiaMobileAppBannerComponent,
    BottleComponent,
    UpgradeComponent,
    ShortcutsComponent,
    RecipesTextComponent,
    WaterMacroBarComponent,
    MacrosBarComponent,
    ValidationErrorComponent,
    BodySectionComponent,
    FriendsSectionComponent,
    PersonalInfoSectionComponent,
    TrainingInfoSectionComponent,
    DeleteSectionComponent,
    ToggleSwitchComponent,
    LoadingComponent,
    ModalHeaderComponent,
    CheckboxComponent,
    DatepickerComponent,
    NutrientsBreakdownComponent,
    SwiperComponent,
    PopupComponent,
    TooltipComponent,
    OptionsListComponent
];

// EVENT EMITTERS
import {AuthEventsEmitter} from './events/auth.events';
import {AppLoadingEventsEmitter} from './events/loading.events';
import {LocalisationEventsEmitter} from './events/localisation.events';
import {BsModalEventsEmitter} from './events/bsModal.events';
import {PopupEventsEmitter} from './events/popup.events';
import {TooltipEventsEmitter} from './events/tooltip.events';

// EVENT EMITTERS
const EVENT_EMITTERS = [
    AuthEventsEmitter,
    AppLoadingEventsEmitter,
    LocalisationEventsEmitter,
    TriggerActionEventsEmitter,
    BsModalEventsEmitter,
    PopupEventsEmitter,
    TooltipEventsEmitter
];

// SERVICES
import {AuthService} from './services/auth.service';
import {LocalisationService} from './services/localisation.service';
import {SocialService} from './services/social.service';
import {CountriesService} from './services/countries.service';
import {NotificationsService} from './services/notification.service';
import {UsersService} from './services/users.service';
import {StorageService} from './services/storage.service';
import {DynamicComponentsService} from './services/dynamicComponents.service';
import {DiaryService} from './services/diary.service';
import {ProductsService} from './services/products.service';
import {WebsiteService} from './services/website.service';
import {DateService} from './services/date.service';

// SERVICES
const SERVICES = [
    ThemeService,
    AuthService,
    LocalisationService,
    SocialService,
    CountriesService,
    NotificationsService,
    UsersService,
    MasterService,
    StorageService,
    PricingService,
    DynamicComponentsService,
    DiaryService,
    PageService,
    ProductsService,
    WebsiteService,
    NewsService,
    CateringService,
    StarsService,
    DateService,
    FriendsService,
];

// PROVIDERS
import {CONSTANTS_PROVIDERS} from './providers/constants.providers';
import {FACEBOOK_PROVIDERS} from './providers/facebook.providers';
import {SWIPER_PROVIDERS} from './providers/swiper.providers';

// PROVIDERS
const PROVIDERS = [
    ...BRAINTREE_BG_TRANSLATION_PROVIDER,
    ...BRAINTREE_EN_TRANSLATION_PROVIDER,
    ...CONSTANTS_PROVIDERS,
    ...FACEBOOK_PROVIDERS,
    ...SWIPER_PROVIDERS,
    BsModalRef
];

// GUARDS
import {AuthGuard} from './guards/auth.guard';
import {UnauthGuard} from './guards/unauth.guard';
import {WebRegistrationClosedGuard} from './guards/web-registration-closed.guard';
import {WizardGuard} from './guards/wizard.guard';
import {WizardReadyGuard} from './guards/wizardReady.guard';
import {PricingGuard} from './guards/pricing.guard';
import {NoPricingGuard} from './guards/noPricing.guard';

// GUARDS
const GUARDS = [
    AuthGuard,
    UnauthGuard,
    WebRegistrationClosedGuard,
    WizardGuard,
    WizardReadyGuard,
    PricingGuard,
    NoPricingGuard
];

// DIRECTIVES
import {InputValidationDirective} from './directives/validation.directive';
import {PopupDirective} from './directives/popup.directive';
import {TooltipDirective} from './directives/tooltip.directive';
import {MasterLockDirective} from './directives/master-lock.directive';

// DIRECTIVES
const DIRECTIVES = [
    InputValidationDirective,
    PopupDirective,
    TooltipDirective,
    MasterLockDirective
];

// PIPES
import {LocalisationPipe} from './pipes/localisation.pipe';
import {MinutesToReadableTimePipe} from './pipes/minutesToReadableTime.pipe';

// PIPES
const PIPES = [
    KeepHtmlPipe,
    CallbackPipe,
    LocalisationPipe,
    MoneyPipe,
    MinutesToReadableTimePipe
];

// INTERCEPTORS
import {JWTInterceptor} from './interceptors/jwt.interceptor';
import {ThemeService} from './services/theme.service';
import {NouisliderModule} from 'ng2-nouislider';
import {AlertsComponent} from './components/alerts/alerts.component';
import {AgaiaMobileAppBannerComponent} from './components/agaia-mobile-app-banner/agaia-mobile-app-banner.component';
import {MacrosBarComponent} from './components/macros-bar/macros-bar.component';
import {PageService} from './services/page.service';
import {KeepHtmlPipe} from './pipes/keepHtml.pipe';
import {CallbackPipe} from './pipes/callback.pipe';
import {BRAINTREE_BG_TRANSLATION_PROVIDER} from './providers/braintree.bg.translation.providers';
import {BRAINTREE_EN_TRANSLATION_PROVIDER} from './providers/braintree.en.translation.providers';
import {FriendsSectionComponent} from './components/profile-sections/friends/friends-section.component';
import {FriendsService} from './services/friends.service';
import {BottleComponent} from './components/bottle/bottle.component';
import {NewsService} from './services/news.service';
import {DeleteSectionComponent} from './components/profile-sections/delete-section/delete-section.component';
import {MoneyPipe} from './pipes/money.pipe';
import {WaterMacroBarComponent} from './components/water-macro-bar/water-macro-bar.component';
import {DialCodeSelectorModalComponent} from './components/profile-sections/dial-code-selector-modal/dial-code-selector-modal.component';
import {PricingService} from '../pricing/services/pricing.service';
import {UpgradeComponent} from './components/modals/upgrade/upgrade.component';
import {StarsService} from './services/stars.service';
import {MobilebarComponent} from './components/mobilebar/mobilebar.component';
import {ShortcutsComponent} from './components/modals/shortcuts/shortcuts.component';
import {TriggerActionEventsEmitter} from './events/triggerAction.events';
import {RecipesTextComponent} from './components/modals/recipes-text/recipes-text.component';
import {LazyLoadImageModule} from 'ng-lazyload-image';
import {MasterService} from './services/master.service';
import {CateringService} from './services/catering.service';

// INTERCEPTORS
const INTERCEPTORS = [
    {
        provide: HTTP_INTERCEPTORS,
        useClass: JWTInterceptor,
        multi: true
    }
];

@NgModule({
    imports: [
        ...MODULES,
        ...MODULES_WITH_PROVIDERS,
        NouisliderModule,
        LazyLoadImageModule
    ],
    declarations: [
        ...COMPONENTS,
        ...DIRECTIVES,
        ...PIPES
    ],
    entryComponents: [
        ...ENTRY_COMPONENTS
    ],
    exports: [
        ...MODULES,
        ...COMPONENTS,
        ...DIRECTIVES,
        ...PIPES
    ]
})
export class SharedModule {
    static forRoot(): ModuleWithProviders {
        return {
            ngModule: SharedModule,
            providers: [
                ...PIPES,
                ...SERVICES,
                ...PROVIDERS,
                ...GUARDS,
                ...EVENT_EMITTERS,
                ...INTERCEPTORS
            ]
        };
    }
}
